import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // Decode JWT token to get user info (more reliable than localStorage)
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken) {
      setToken(storedToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // Decode token to get user info (including emailVerified status)
      // Don't call API here - let individual pages decide when to refresh
      const decoded = decodeToken(storedToken);
      if (decoded) {
        const userFromToken = {
          id: decoded.userId,
          email: decoded.email,
          emailVerified: decoded.emailVerified || false,
        };
        setUser(userFromToken);
        // Update localStorage with info from token
        localStorage.setItem('user', JSON.stringify(userFromToken));
      } else if (storedUser) {
        // Fallback to stored user if token decode fails
        setUser(JSON.parse(storedUser));
      }
    }
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { 
      email, 
      password,
      subdomain: 'test' // For local development
    });
    const { token: newToken, user: newUser } = response.data;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const register = async (email, password, captchaToken, firstName, lastName) => {
    const response = await api.post('/auth/register', { 
      email, 
      password,
      captchaToken,
      firstName,
      lastName,
      subdomain: 'test' // For local development
    });
    const { token: newToken, user: newUser } = response.data;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  // Function to refresh user data from server (gets latest email verification status)
  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        // Fetch latest user data from server
        const response = await api.get('/auth/me');
        if (response.data.user && response.data.token) {
          setUser(response.data.user);
          setToken(response.data.token);
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        const status = error?.response?.status;
        if (status === 401 || status === 404) {
          // Token is invalid or user not found in this environment
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete api.defaults.headers.common['Authorization'];
          return;
        }
        // Fallback to token decode if API call fails
        const decoded = decodeToken(storedToken);
        if (decoded) {
          const userFromToken = {
            id: decoded.userId,
            email: decoded.email,
            emailVerified: decoded.emailVerified || false,
          };
          setUser(userFromToken);
          localStorage.setItem('user', JSON.stringify(userFromToken));
        }
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        login,
        register,
        logout,
        refreshUser,
        setToken, // Expose setToken so VerifyEmail can update it
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook exported from same file as provider
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

